import type { Vec } from '@blorbs/common';
import type { Camera } from './render/camera';

/**
 * Keyboard + mouse on desktop; on touch, the left half of the screen is a
 * drag-anywhere joystick and a tap on the right half shoots toward the tap.
 */
export class Input {
  move: Vec = { x: 0, y: 0 };
  /** world-space aim direction (unit) — where the orb looks */
  aim: Vec = { x: 1, y: 0 };
  /** set by click/tap; consumed by the game loop */
  shootRequested = false;
  /** world point to teleport toward, set by Space; consumed by the game loop */
  private teleportTarget: Vec | null = null;
  /** set by right click; consumed by the game loop */
  private shockwaveRequested = false;
  /** direction captured at the moment of a tap/click, so a joystick move can't overwrite it before the shot fires */
  private shotDir: Vec | null = null;
  /** the kind of pointer used last; decides whether the mouse drives aim */
  private lastPointerType = 'mouse';
  /** any key/click — used to dismiss things or restart */
  anyPress = false;

  readonly isTouch = matchMedia('(pointer: coarse)').matches;
  /** for drawing the virtual joystick */
  joystick: { origin: Vec; knob: Vec } | null = null;

  private keys = new Set<string>();
  private mouse: Vec = { x: 0, y: 0 };
  private joyPointer: number | null = null;
  private readonly JOY_RADIUS = 56;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: Camera,
    private readonly getMyPos: () => Vec | null,
  ) {
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      this.keys.add(e.code);
      this.anyPress = true;
      if (e.code === 'Space') {
        // Teleport toward the mouse (Warlock: point-targeted, range-clamped server-side).
        this.teleportTarget = this.camera.toWorld(this.mouse.x, this.mouse.y);
        e.preventDefault();
      }
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());

    canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    canvas.addEventListener('pointermove', (e) => this.onPointerMove(e));
    canvas.addEventListener('pointerup', (e) => this.onPointerUp(e));
    canvas.addEventListener('pointercancel', (e) => this.onPointerUp(e));
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private onPointerDown(e: PointerEvent): void {
    this.anyPress = true;
    this.lastPointerType = e.pointerType;
    if (e.pointerType === 'touch') {
      // First touch on the left half is the joystick; any other touch is a shot toward the tap.
      if (e.clientX < window.innerWidth / 2 && this.joyPointer === null) {
        this.joyPointer = e.pointerId;
        this.joystick = { origin: { x: e.clientX, y: e.clientY }, knob: { x: e.clientX, y: e.clientY } };
        this.canvas.setPointerCapture(e.pointerId);
      } else {
        this.shootToward(e.clientX, e.clientY);
      }
      return;
    }
    this.mouse = { x: e.clientX, y: e.clientY };
    if (e.button === 2) this.shockwaveRequested = true; // right click
    else if (e.button === 0) this.shootToward(e.clientX, e.clientY);
  }

  /** Queue a shot in the direction of a screen point, and face that way. */
  private shootToward(sx: number, sy: number): void {
    const dir = this.dirToScreen(sx, sy);
    if (!dir) return;
    this.aim = dir;
    this.shotDir = dir;
    this.shootRequested = true;
  }

  private onPointerMove(e: PointerEvent): void {
    if (e.pointerType !== 'touch') this.lastPointerType = e.pointerType;
    if (e.pointerType === 'touch') {
      if (e.pointerId !== this.joyPointer || !this.joystick) return;
      const dx = e.clientX - this.joystick.origin.x;
      const dy = e.clientY - this.joystick.origin.y;
      const d = Math.hypot(dx, dy);
      const clamped = Math.min(d, this.JOY_RADIUS);
      const nx = d === 0 ? 0 : dx / d;
      const ny = d === 0 ? 0 : dy / d;
      this.joystick.knob = { x: this.joystick.origin.x + nx * clamped, y: this.joystick.origin.y + ny * clamped };
      const strength = clamped / this.JOY_RADIUS;
      this.move = { x: nx * strength, y: ny * strength };
      // Face the way you're going when there's no explicit aim.
      if (strength > 0.2) this.aim = { x: nx, y: ny };
      return;
    }
    this.mouse = { x: e.clientX, y: e.clientY };
  }

  private onPointerUp(e: PointerEvent): void {
    if (e.pointerId === this.joyPointer) {
      this.joyPointer = null;
      this.joystick = null;
      this.move = { x: 0, y: 0 };
    }
  }

  /** Unit vector from the orb to a screen point, or null if we don't know where we are yet. */
  private dirToScreen(sx: number, sy: number): Vec | null {
    const me = this.getMyPos();
    if (!me) return null;
    const w = this.camera.toWorld(sx, sy);
    const dx = w.x - me.x;
    const dy = w.y - me.y;
    const d = Math.hypot(dx, dy);
    return d > 0 ? { x: dx / d, y: dy / d } : null;
  }

  /** Call once per frame before reading `move` / `aim`. */
  poll(): void {
    const k = this.keys;
    let x = 0;
    let y = 0;
    if (k.has('KeyW') || k.has('ArrowUp')) y -= 1;
    if (k.has('KeyS') || k.has('ArrowDown')) y += 1;
    if (k.has('KeyA') || k.has('ArrowLeft')) x -= 1;
    if (k.has('KeyD') || k.has('ArrowRight')) x += 1;
    const l = Math.hypot(x, y);
    // Keys steer unless the joystick is held (it sets `move` itself in onPointerMove).
    if (l > 0) this.move = { x: x / l, y: y / l };
    else if (this.joyPointer === null) this.move = { x: 0, y: 0 };
    // The mouse drives aim continuously; on touch, aim follows the joystick / last tap.
    if (this.lastPointerType === 'mouse') {
      const dir = this.dirToScreen(this.mouse.x, this.mouse.y);
      if (dir) this.aim = dir;
    }
  }

  /** From the ability card (touch): fire the shockwave. */
  requestShockwave(): void {
    this.shockwaveRequested = true;
  }

  /** From the ability card (touch): blink the way we're facing, full range. */
  requestTeleportAlongAim(): void {
    const me = this.getMyPos();
    if (!me) return;
    this.teleportTarget = { x: me.x + this.aim.x * 10000, y: me.y + this.aim.y * 10000 };
  }

  consumeShockwave(): boolean {
    const s = this.shockwaveRequested;
    this.shockwaveRequested = false;
    return s;
  }

  /** The world point to teleport toward if Space was pressed since the last call, else null. */
  consumeTeleport(): Vec | null {
    const t = this.teleportTarget;
    this.teleportTarget = null;
    return t;
  }

  /** The direction to shoot if a shot was requested since the last call, else null. */
  consumeShoot(): Vec | null {
    if (!this.shootRequested) return null;
    this.shootRequested = false;
    const dir = this.shotDir ?? this.aim;
    this.shotDir = null;
    return dir;
  }

  consumeAnyPress(): boolean {
    const s = this.anyPress;
    this.anyPress = false;
    return s;
  }
}
